// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    tryUpdateIntegrationRequest,
    UpdateIntegrationRequest,
    UpdateIntegrationRequestDataType,
} from '../../actions/integrations';
import { getError, getIsLoading, getUpdated } from '../../helpers';
import { integrationUpdatableForIntegrationType } from '../../helpers/integrationHelpers';
import { Integration, IntegrationInstallationId, IntegrationType } from '../../types';
import { AppComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';

interface useUpdateIntegrationModalData {
    setShowUpdateIntegrationModal: React.Dispatch<React.SetStateAction<boolean>>;
    updateIntegrationModalProps: UpdateIntegrationModalData;
}

export interface UpdateIntegrationModalData extends AppComponentProps {
    closable: boolean;
    isLoading: boolean;
    open: boolean;
    integration: Integration;
    updateIntegration: (
        integrationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => void;
}

interface UpdateIntegrationModalProps extends AppComponentProps {
    selectedIntegration: Integration;
    closable?: boolean;
    onUpdateIntegrationSuccess?: () => void;
}

export const useUpdateIntegrationModal = ({
    orgId,
    appId,
    selectedIntegration,
    closable = true,
    onUpdateIntegrationSuccess,
}: UpdateIntegrationModalProps): useUpdateIntegrationModalData => {
    const dispatch = useDispatch();

    const [showIntegrationConfigurationModal, setShowIntegrationConfigurationModal] = React.useState(false);
    const [integrationSetupType, setIntegrationSetupType] = React.useState(null);

    const updateIntegrationIsLoading = useSelector((state: RootState) =>
        getIsLoading(integrationSetupType, state, selectedIntegration?.installationId),
    );
    const updateIntegrationSuccess = useSelector((state: RootState) =>
        getUpdated(integrationSetupType, state, selectedIntegration?.installationId),
    );
    const updateIntegrationError = useSelector((state: RootState) =>
        getError(integrationSetupType, state, selectedIntegration?.installationId),
    );

    const updateIntegration = (
        integrationInstallationId: IntegrationInstallationId,
        integrationId: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => {
        const type = integrationUpdatableForIntegrationType(integrationId);
        setIntegrationSetupType(type);

        const updateRequest: UpdateIntegrationRequest = {
            orgId,
            appId,
            integrationId: integrationInstallationId,
            data,
        };

        if (type) {
            dispatch(tryUpdateIntegrationRequest(type, updateRequest));
        } else {
            globalMessage.error('This integration does not support updating.');
        }
    };

    React.useEffect(() => {
        if (updateIntegrationSuccess) {
            setShowIntegrationConfigurationModal(false);
            if (onUpdateIntegrationSuccess) {
                onUpdateIntegrationSuccess();
            }
            globalMessage.success('Integration updated!');
        }
    }, [updateIntegrationSuccess]);

    React.useEffect(() => {
        if (updateIntegrationError) {
            globalMessage.error(updateIntegrationError);
        }
    }, [updateIntegrationError]);

    const updateIntegrationModalProps = React.useMemo(() => {
        return {
            orgId,
            appId,
            closable,
            isLoading: updateIntegrationIsLoading,
            integration: selectedIntegration,
            open: showIntegrationConfigurationModal,
            updateIntegration,
        };
    }, [orgId, appId, showIntegrationConfigurationModal, updateIntegrationIsLoading, selectedIntegration]);

    return { setShowUpdateIntegrationModal: setShowIntegrationConfigurationModal, updateIntegrationModalProps };
};
